import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ChangeDetectionStrategy,
  ComponentRef,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, switchMap, of, finalize, shareReplay, map } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { DprDetailsPageBaseComponent } from '@dpr-details/models/component';
import { DprPersonnelDetailGridData } from '@dpr-details/models/grid';
import { DprDetailsService } from '@dpr-details/services';
import { DprPersonnelGridService } from '@dpr-details/services/grid';
import { getDefaultSubMenuItems } from '@dpr-details/utils';
import { PersonnelVersion, StaticMessage } from '@shared/enums';
import { DprDetailsSync, DprPersonnel } from '@shared/models/dpr';
import { DprAttachmentsGridData, Sync } from '@shared/models/grid';
import { ScreenSizeService, ToastNotificationService } from '@shared/services';
import { getStaticMessage } from '@shared/utils';
import { DprPersonnelRegistrationsGridComponent } from '@dpr-details/components';
import { DprDetailsSyncModelBuilderService } from '@dpr-details/services/sync';
import { DprPersonnelGridSync } from '@dpr-details/models/sync';
import { DprAttachmentService } from '@shared/services/dpr';
import { NavigationMenuItem } from '@shared/models';

@UntilDestroy()
@Component({
  selector: 'app-dpr-personnel-page',
  templateUrl: './dpr-personnel-page.component.html',
  styleUrls: ['./dpr-personnel-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprPersonnelPageComponent
  extends DprDetailsPageBaseComponent<DprPersonnelDetailGridData>
  implements OnInit
{
  labeling = {
    gridEntityName: $localize`:@@dprDetails.dprPersonnelPage.txtPersonnelRegistrations:Personnel Registrations`,
  };

  StaticMessage = StaticMessage;

  preselectedItem: string | number;
  navigationSelectItems: NavigationMenuItem[] = getDefaultSubMenuItems();

  constructor(
    private dprDetailsService: DprDetailsService,
    protected activatedRoute: ActivatedRoute,
    protected dprAttachmentService: DprAttachmentService,
    private dprPersonnelGridService: DprPersonnelGridService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    private toastNotificationService: ToastNotificationService,
    protected screenSizeService: ScreenSizeService
  ) {
    super(activatedRoute, dprAttachmentService);
  }

  private get dprPersonnel(): DprPersonnel {
    return this.dprDetails.dprPersonnel;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.preselectedItem =
      this.activatedRoute?.firstChild?.snapshot?.url[0]?.path;
  }

  save(
    _?,
    isAutoSync = false,
    isAutoSyncFromAdminChange = false
  ): Observable<boolean> {
    if (!this.isDirty$.value || this.isSaving) {
      throw new Error(
        'Unable to save data. There are either no changes to be saved or there is already a save in progress'
      );
    }

    this.isSaving = true;

    if (
      this.dprPersonnelGridService.shouldTriggerAutoFill(
        this.registrationsGridData,
        this.selectedSubproject?.data
      )
    ) {
      this.autoFillEmptyItems();
    }

    // store activated component changes
    this.storeComponentChanges(this.activatedComponent);

    const validateChanges$ =
      isAutoSync || isAutoSyncFromAdminChange
        ? of(true)
        : this.validateChanges(
            this.registrationsChanges,
            this.attachmentsChanges
          );

    const saveChanges$ = validateChanges$.pipe(
      untilDestroyed(this),
      switchMap((valid) => {
        if (!valid) {
          return of(false);
        }

        return this.saveChanges(
          this.registrationsChanges,
          this.attachmentsChanges,
          this.comment,
          isAutoSyncFromAdminChange
        );
      }),
      finalize(() => {
        this.isSaving = false;
      }),
      shareReplay()
    );

    saveChanges$.pipe(untilDestroyed(this)).subscribe();

    return saveChanges$.pipe(map((result) => !!result));
  }

  onSelectionChange(currentSelection: string | number): void {
    this.preselectedItem = currentSelection;
  }

  protected isRegistrationComponent(component: ComponentRef<any>): boolean {
    return component instanceof DprPersonnelRegistrationsGridComponent;
  }

  protected setupData(): void {
    this.registrationsGridData = this.dprPersonnelGridService.buildGridData(
      this.selectedSubproject?.data,
      this.dprPersonnel?.dprPersonnelDetails
    );
    this.attachmentsGridData = this.dprAttachmentService.mapToGridData(
      this.dprPersonnel?.dprPersonnelAttachments
    );
    this.comment = this.dprPersonnel.comment;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected getData(): void {}

  protected getOfflineRegistrationsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): Sync<DprPersonnelDetailGridData> {
    const offlineChanges =
      dprDetailsOfflineChanges?.dprPersonnel?.syncDprPersonnelDetails;
    if (!offlineChanges) {
      return null;
    }

    const offlineGridData = this.dprPersonnelGridService.buildGridData(
      this.selectedSubproject?.data,
      [
        ...(offlineChanges.addedList ?? []),
        ...(offlineChanges.updatedList ?? []),
      ]
    );

    return this.getOfflineGridDataChanges(offlineGridData, offlineChanges);
  }

  protected getOfflineAttachmentsChanges(
    dprDetailsOfflineChanges: DprDetailsSync
  ): Sync<DprAttachmentsGridData> {
    const offlineChanges =
      dprDetailsOfflineChanges?.dprPersonnel?.syncDprPersonnelAttachments;
    return this.getOfflineAttachmentGridDataChanges(offlineChanges);
  }

  //#region Private methods

  private validateChanges(
    _registrationsChanges: Sync<DprPersonnelDetailGridData>,
    attachmentsChanges: Sync<DprAttachmentsGridData>
  ): Observable<boolean> {
    if (!this.dprAttachmentService.validateChanges(attachmentsChanges)) {
      return of(false);
    }

    return of(true);
  }

  private saveChanges(
    registrationsChanges: Sync<DprPersonnelDetailGridData>,
    attachmentsChanges: Sync<DprAttachmentsGridData>,
    comment: string,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprPersonnel> {
    const syncDprPersonnelDetails = this.dprPersonnelGridService.mapToGridSync(
      this.dprPersonnel.id,
      registrationsChanges
    );

    const syncData: DprPersonnelGridSync =
      this.dprDetailsSyncModelBuilderService.forDprPersonnel(
        this.selectedSubproject.id,
        this.dprDetails.id,
        this.dprPersonnel.id,
        syncDprPersonnelDetails,
        attachmentsChanges,
        comment
      );

    const saveChanges$ = this.dprDetailsService
      .syncDprPersonnel(syncData, isAutoSyncFromAdminChange)
      .pipe(shareReplay());

    saveChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.onSaveSuccess(result);
      },
      error: (error: HttpErrorResponse) => {
        this.saveErrorHandlerService.onSaveError(
          this.customTabNames.personnelTabName,
          this.selectedSubproject.id,
          error
        );
      },
    });

    return saveChanges$;
  }

  private onSaveSuccess(result: DprPersonnel): void {
    this.refreshData();
    this.offlineFacade.setDprPersonnel(result, this.dprDetails.id);
  }

  private autoFillEmptyItems(): void {
    if (!this.registrationsGridData?.length) {
      return;
    }

    this.toastNotificationService.info(
      getStaticMessage(
        StaticMessage.AutoFillText,
        this.customTabNames.personnelTabName
      ),
      false,
      getStaticMessage(
        StaticMessage.AutoFillTitle,
        this.customTabNames.personnelTabName
      )
    );

    const personnelVersion =
      this.selectedSubproject?.data?.subprojectConfiguration.personnelVersion;

    switch (personnelVersion) {
      case PersonnelVersion.Basic:
        this.registrationsGridData.forEach((x) => {
          if (x.today == null) {
            this.registrationsGridApi.editCell(x, 'today', 0);
          }
        });
        break;
      case PersonnelVersion.Stock:
        this.registrationsGridData.forEach((x) => {
          if (x.added == null) {
            this.registrationsGridApi.editCell(x, 'added', 0);
          }
          if (x.used == null) {
            this.registrationsGridApi.editCell(x, 'used', 0);
          }
        });
        break;
      case PersonnelVersion.Classic:
        this.registrationsGridData.forEach((x) => {
          if (x.currentDay == null) {
            this.registrationsGridApi.editCell(x, 'currentDay', 0);
          }
        });
        break;
    }

    this.registrationsChanges = this.registrationsGridApi.getChanges();
  }

  //#endregion
}
